import { z } from 'zod'

import { apiRequest } from '@/lib/api/client'
import { ApiError } from '@/lib/api/errors'
import { env } from '@/lib/env'
import { toE164 } from '@/lib/format'

import { readGoogleIdTokenClaims } from './gsi'

import { mapLoginError } from './login-error'
import {
  accountProfileSchema,
  googleLoginRequestSchema,
  loginTokenSchema,
  toSession,
  toUserFromProfile,
  type AccountProfile,
  type LoginInput,
  type LoginToken,
  type Session,
  type User,
} from './schema'

const CUSTOMER_ROLE = 'CUSTOMER'

function bearerHeaders(accessToken: string) {
  return { Authorization: `Bearer ${accessToken}` }
}

async function sessionFromToken(token: LoginToken, fallbackId: string): Promise<Session> {
  let user: User
  try {
    user = await fetchCurrentUser(undefined, token.token)
  } catch {
    user = toUserFromProfile(token as Partial<AccountProfile>, fallbackId)
    if (token.account_id != null) user = { ...user, id: String(token.account_id) }
  }
  return toSession(token, user)
}

export async function login({ dialCode, phone, password }: LoginInput): Promise<Session> {
  const phoneNumber = toE164(dialCode, phone)

  try {
    const token = await apiRequest('/v3/account/auth/login', {
      method: 'POST',
      schema: loginTokenSchema,
      skipAuth: true,
      // Android `LoginRequest`: phone_number + password + role CUSTOMER.
      body: {
        phone_number: phoneNumber,
        password,
        role: CUSTOMER_ROLE,
        token_reactivate: null,
      },
    })

    return sessionFromToken(token, phoneNumber)
  } catch (error) {
    throw mapLoginError(error)
  }
}

/**
 * Exchange a Google ID token (GIS `credential` JWT) for a Lion Parcel session.
 * Account `POST /v2/account/auth/mail/login` — WEBLP-2291 / WEBLP-2205.
 */
export async function loginWithGoogle(idToken: string): Promise<Session> {
  const tokenJwt = idToken.trim()
  if (!tokenJwt) {
    throw new Error('Token Google tidak tersedia. Coba lagi.')
  }

  if (import.meta.env.DEV) {
    const claims = readGoogleIdTokenClaims(tokenJwt)
    console.info('[gis] JWT aud', claims?.aud ?? '(unreadable)')
    console.info('[gis] VITE_GOOGLE_CLIENT_ID', env.VITE_GOOGLE_CLIENT_ID)
  }

  try {
    const token = await apiRequest('/v2/account/auth/mail/login', {
      method: 'POST',
      schema: loginTokenSchema,
      skipAuth: true,
      body: googleLoginRequestSchema.parse({
        device: 'robojastip-web',
        token: tokenJwt,
      }),
    })

    return sessionFromToken(token, 'google')
  } catch (error) {
    throw mapGoogleLoginError(error)
  }
}

function mapGoogleLoginError(error: unknown): Error {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error : new Error('Login Google gagal.')
  }

  const message = error.message.toLowerCase()
  if (error.status === 422 && message.includes('device')) {
    return new Error(
      'Layanan account belum menerima device robojastip-web. Tunggu deploy WEBLP-2291.',
    )
  }
  if (error.status !== 400) {
    return error
  }
  if (message.includes('email tidak terdaftar') || message.includes('email not registered')) {
    return new Error('Email Google belum terdaftar di Lion Parcel.')
  }
  if (message.includes('token tidak valid') || message.includes('invalid token')) {
    return new Error(
      'Token Google ditolak layanan account. web_robojastip_client_id di backend harus sama dengan VITE_GOOGLE_CLIENT_ID.',
    )
  }
  return error
}

function flattenProfile(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw
  const record = raw as Record<string, unknown>
  for (const key of ['account', 'profile', 'customer', 'user', 'data'] as const) {
    const nested = record[key]
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return { ...record, ...nested }
    }
  }
  return raw
}

export async function fetchCurrentUser(signal?: AbortSignal, accessToken?: string): Promise<User> {
  const raw = await apiRequest('/v2/account/profile/me', {
    schema: z.unknown(),
    signal,
    ...(accessToken ? { skipAuth: true, headers: bearerHeaders(accessToken) } : {}),
  })
  const profile = accountProfileSchema.parse(flattenProfile(raw))
  return toUserFromProfile(profile, asProfilePhone(profile))
}

function asProfilePhone(profile: { phone_number?: string | number | null }) {
  if (profile.phone_number == null) return ''
  return String(profile.phone_number).trim()
}

/**
 * Logout is client-side only, matching the Android consumer
 * (`AuthenticationHelper.logout` just drops the local session).
 * `POST /v1/account/auth/logout` is scoped to `v1.courier.logout.POST` in the
 * account service, so a CUSTOMER token gets 403 there.
 */
export function logout(): Promise<void> {
  return Promise.resolve()
}
